import { Box, Text } from '@mantine/core';

/**
 * SystemMessage — platform-generated thread event (state changes, address
 * reveals, cash nudges, etc).
 *
 * Per design pack §5.5 + voice doc: centred, factual. Each event renders as a
 * single contained, icon-led box so a reader scrolling the thread can tell
 * "host confirmed" from "dispute opened" at a glance — the body copy used to be
 * the only differentiator which made high-stakes events look identical to
 * gentle nudges. Glyphs were replaced with crisp 1.6-stroke SVG icons.
 *
 * Cash nudges and other covenant-aligned messages keep the calmest treatment
 * (the subtlest tint + an Ink-neutral accent); their tone differentiation lives
 * in the body copy, not the chrome.
 *
 * The box tint uses color-mix so it reads on BOTH the plain page background
 * (Storybook) and the thread's gradient panel.
 */
export type SystemMessageKind =
  | 'state_change'
  | 'verification_unlock'
  | 'review_unlock'
  | 'cash_nudge'
  | 'dispute_opened'
  | 'dispute_resolved'
  | 'address_revealed';

export type SystemMessageProps = {
  body: string;
  kind: SystemMessageKind | null;
  createdAt: number;
};

/* ------------------------------------------------------------------ */
/* Icons — 24×24 viewBox, 1.6 stroke, round caps/joins, currentColor.  */
/* Decorative: aria-hidden + focusable={false}. Stroke colour comes    */
/* from the parent via `color` so each icon inherits its kind accent.  */
/* ------------------------------------------------------------------ */

type IconProps = { size?: number };

function svgProps(size: number) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
  };
}

function CheckIcon({ size = 17 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.4l2.4 2.4 4.6-5" />
    </svg>
  );
}

function ShieldCheckIcon({ size = 17 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function StarIcon({ size = 17 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 18.7 6.6 19.5l1.2-6L3.3 9.3l6.1-.7z" />
    </svg>
  );
}

function BanknoteIcon({ size = 17 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M3 6h18v12H3z" />
      <circle cx="12" cy="12" r="2.4" />
    </svg>
  );
}

function AlertTriangleIcon({ size = 17 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M10.3 3.7 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.7a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function MapPinIcon({ size = 17 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11Z" />
      <circle cx="12" cy="10" r="2.4" />
    </svg>
  );
}

function InfoIcon({ size = 17 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01" />
      <path d="M11 12h1v4h1" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Per-kind presentation. Accent hex stays on-brand in both schemes;   */
/* tints/borders are derived from it via color-mix so a single value    */
/* drives the whole chip. Weights match the design contract.            */
/* ------------------------------------------------------------------ */

type KindPresentation = {
  accent: string;
  weight: number;
  Icon: (p: IconProps) => React.JSX.Element;
};

const KIND_PRESENTATION: Record<SystemMessageKind, KindPresentation> = {
  state_change: { accent: '#D2602E', weight: 600, Icon: CheckIcon },
  verification_unlock: { accent: '#2A7E78', weight: 600, Icon: ShieldCheckIcon },
  review_unlock: { accent: '#E9A23B', weight: 600, Icon: StarIcon },
  cash_nudge: { accent: '#807B74', weight: 500, Icon: BanknoteIcon },
  dispute_opened: { accent: '#A33B00', weight: 700, Icon: AlertTriangleIcon },
  dispute_resolved: { accent: '#2A7E78', weight: 600, Icon: CheckIcon },
  address_revealed: { accent: '#2A7E78', weight: 600, Icon: MapPinIcon },
};

const FALLBACK_PRESENTATION: KindPresentation = {
  accent: '#807B74',
  weight: 500,
  Icon: InfoIcon,
};

/* cash_nudge + the null fallback keep the subtlest chrome. */
const CALMEST: ReadonlySet<SystemMessageKind | null> = new Set([
  'cash_nudge',
  null,
]);

export function SystemMessage({ body, kind, createdAt }: SystemMessageProps) {
  const time = formatTime(createdAt);
  const pres = kind === null ? FALLBACK_PRESENTATION : KIND_PRESENTATION[kind];
  const { accent, weight, Icon } = pres;

  const tintPct = CALMEST.has(kind) ? '5%' : '8%';

  return (
    <Box
      my="md"
      px="md"
      ta="center"
      data-testid="system-message"
      data-kind={kind ?? 'unknown'}
    >
      <Box
        style={{
          marginInline: 'auto',
          maxWidth: 540,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          borderRadius: 12,
          textAlign: 'left',
          background: `color-mix(in srgb, ${accent} ${tintPct}, transparent)`,
          border: `1px solid color-mix(in srgb, ${accent} 22%, transparent)`,
          boxShadow:
            'light-dark(0 1px 2px rgba(33,28,22,0.06), 0 1px 2px rgba(0,0,0,0.30))',
        }}
      >
        {/* Leading icon chip */}
        <Box
          aria-hidden
          style={{
            flex: '0 0 auto',
            width: 28,
            height: 28,
            borderRadius: 9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accent,
            background: `color-mix(in srgb, ${accent} 16%, transparent)`,
          }}
        >
          <Icon size={17} />
        </Box>

        {/* Body + timestamp */}
        <Box style={{ minWidth: 0 }}>
          <Text
            component="div"
            size="sm"
            fw={weight}
            style={{
              whiteSpace: 'pre-wrap',
              color: 'var(--mantine-color-text)',
            }}
          >
            {body}
          </Text>
          <Text size="xs" c="dimmed" mt={2}>
            {time}
          </Text>
        </Box>
      </Box>
    </Box>
  );
}

function formatTime(ms: number): string {
  try {
    return new Date(ms).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
